import * as t from 'io-ts';
import { otsBaseApi } from '../../ots-api';
import {
  isUserProcessActive,
  SubmitUserProcessArgs,
  UserProcess,
  UserProcessCodec,
} from '../../../model/user-process';
import { validateJSONNow } from '../../../web/validation';

const TAG = 'UserProcesses' as const;

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: [TAG],
});

/**
 * The signed-in person's processes (`/processes`). The list stays current through
 * {@link connectUserProcessStream}, which upserts changes into the `getMyUserProcesses` cache.
 */
export const userProcessApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMyUserProcesses: builder.query<UserProcess[], void>({
      query: () => ({
        url: `/processes/mine`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        validateJSONNow(t.array(UserProcessCodec), response),
      providesTags: (result) => [
        ...(result ?? []).map(({ id }) => ({ type: TAG, id } as const)),
        { type: TAG, id: 'LIST' },
      ],
    }),
    getUserProcess: builder.query<UserProcess, string>({
      query: (id) => ({
        url: `/processes/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        validateJSONNow(UserProcessCodec, response),
      providesTags: (result, error, id) => [{ type: TAG, id }],
    }),
    /**
     * Report warnings saved with a process, as a `DiagnosticRun` (render it with the shared diagnostics UI).
     * Only call it for a process with `hasDiagnostics`.
     */
    getUserProcessDiagnostics: builder.query<unknown, string>({
      query: (id) => ({
        url: `/processes/${id}/diagnostics`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: TAG, id: `${id}:diagnostics` }],
    }),
    submitUserProcess: builder.mutation<UserProcess, SubmitUserProcessArgs>({
      query: ({ processKey, input, notifyEmail }) => ({
        url: `/processes/${encodeURIComponent(processKey)}`,
        method: 'POST',
        params: { notifyEmail: notifyEmail ?? false },
        body: input,
      }),
      transformResponse: (response: unknown) =>
        validateJSONNow(UserProcessCodec, response),
      invalidatesTags: [{ type: TAG, id: 'LIST' }],
    }),
    cancelUserProcess: builder.mutation<UserProcess, string>({
      query: (id) => ({
        url: `/processes/${id}/cancel`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) =>
        validateJSONNow(UserProcessCodec, response),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(upsertUserProcess(data));
        } catch {
          // the caller handles the failure; nothing to update
        }
      },
    }),
    retryUserProcess: builder.mutation<UserProcess, string>({
      query: (id) => ({
        url: `/processes/${id}/retry`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) =>
        validateJSONNow(UserProcessCodec, response),
      invalidatesTags: [{ type: TAG, id: 'LIST' }],
    }),
    markUserProcessSeen: builder.mutation<{ marked: number }, string>({
      query: (id) => ({
        url: `/processes/${id}/seen`,
        method: 'POST',
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          userProcessApi.util.updateQueryData('getMyUserProcesses', undefined, (draft) => {
            const process = draft.find((candidate) => candidate.id === id);
            if (process && !isUserProcessActive(process)) process.seen = true;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    markAllUserProcessesSeen: builder.mutation<{ marked: number }, void>({
      query: () => ({
        url: `/processes/seen`,
        method: 'POST',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          userProcessApi.util.updateQueryData('getMyUserProcesses', undefined, (draft) => {
            draft.forEach((process) => {
              if (!isUserProcessActive(process)) process.seen = true;
            });
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
  }),
});

/** Puts a process into the cached list (newest first) and its single-process query. */
export const upsertUserProcess = (process: UserProcess) => (dispatch: (action: unknown) => unknown) => {
  dispatch(
    userProcessApi.util.updateQueryData('getMyUserProcesses', undefined, (draft) => {
      const index = draft.findIndex((candidate) => candidate.id === process.id);
      if (index >= 0) {
        draft[index] = process;
      } else {
        draft.unshift(process);
        draft.sort((a, b) => (b.createDttm ?? 0) - (a.createDttm ?? 0));
      }
    })
  );
  dispatch(userProcessApi.util.upsertQueryData('getUserProcess', process.id, process));
};

export const {
  useGetMyUserProcessesQuery,
  useGetUserProcessQuery,
  useGetUserProcessDiagnosticsQuery,
  useSubmitUserProcessMutation,
  useCancelUserProcessMutation,
  useRetryUserProcessMutation,
  useMarkUserProcessSeenMutation,
  useMarkAllUserProcessesSeenMutation,
} = userProcessApi;
